// react components
import React from 'react'
import {
    useDispatch,
    useSelector,
} from 'react-redux'

// data
import {
    defaultReduxState,
    reduxModalErrorEventHandlerSite,
    reduxSalesTicketSite,
} from 'data'

// serializers
import {
    Template816SiteSerializer,
    Variant816HelperSiteSerializer,
} from 'serializers/site'

// props
type VariantHelper816SiteProps = {
    index: number
    object: Variant816HelperSiteSerializer
    onLimitCheck?: (isTryingToExceed: boolean) => void
    product: Template816SiteSerializer
}

// main
export const VariantHelper816Site: React.FC<VariantHelper816SiteProps> = React.memo(({
    index,
    object,
    onLimitCheck,
    product,
}) => {

    const dispatch = useDispatch()
    const reduxCacheSite = useSelector((state: defaultReduxState) => state.reduxCacheSite)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxSalesSite = useSelector((state: defaultReduxState) => state.reduxSalesSite)

    const deviceType = reduxModalSite.deviceType
    const freeUnitPrice = reduxSalesSite.ticketData?.variants?.[index]?.freeUnitPrice || 0
    const qty = reduxSalesSite.ticketData?.variants?.[index]?.qty || 0

    const getRemainingTickets = () => {
        if (!product.count_participant_available) return null
        let currentTotal = 0
        if (reduxSalesSite.ticketData?.variants) {
            Object.values(reduxSalesSite.ticketData.variants).forEach((variant) => {
                const participantQty = variant.variant?.quantity_of_participant_to_register || 1
                currentTotal += (variant.qty || 0) * participantQty
            })
        }
        return Math.max(0, product.count_participant_available - currentTotal)
    }

    const remainingTickets = getRemainingTickets()

    function onSetQty(value: number) {
        try {
            // Calculate current total tickets across all variants (excluding current variant)
            let currentTotal = 0
            if (reduxSalesSite.ticketData?.variants) {
                Object.values(reduxSalesSite.ticketData.variants).forEach((variant, variantIndex) => {
                    if (variantIndex !== index) {
                        const participantQty = variant.variant?.quantity_of_participant_to_register || 1
                        currentTotal += (variant.qty || 0) * participantQty
                    }
                })
            }

            // Check if new total would exceed the limit
            const newTotal = currentTotal + (value * (object.quantity_of_participant_to_register || 1))
            if (product.count_participant_available && newTotal > product.count_participant_available) {
                // Notify parent component about limit attempt
                onLimitCheck?.(true)
                return
            }

            // Notify parent component that we're within limits
            onLimitCheck?.(false)

            const toReturnData = {
                ...reduxSalesSite.ticketData,
                product: product,
                variants: {
                    ...reduxSalesSite.ticketData?.variants,
                    [index]: {
                        freeUnitPrice: freeUnitPrice,
                        index: index,
                        qty: value,
                        variant: object,
                    },
                },
            }
            dispatch(reduxSalesTicketSite(toReturnData))
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'VariantHelper816Site',
                'onSetQty',
            ))
        }
    }

    function onSetFreeUnitPrice(value: number) {
        try {
            const toReturnData = {
                ...reduxSalesSite.ticketData,
                product: product,
                variants: {
                    ...reduxSalesSite.ticketData?.variants,
                    [index]: {
                        freeUnitPrice: value,
                        index: index,
                        qty: qty,
                        variant: object,
                    },
                },
            }
            dispatch(reduxSalesTicketSite(toReturnData))
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'VariantHelper816Site',
                'onSetFreeUnitPrice',
            ))
        }
    }

    return (
        <div className={`t-816-variant ${deviceType}`}>
            <div className={`t-816-variant-name ${deviceType}`}>
                <span>{object.name}</span>
            </div>
            <div className={`t-816-variant-price-wrap ${deviceType}`}>
                {object.is_free_pricing
                    ? (
                        <div className={`t-816-input-wrap ${deviceType}`}>
                            <input
                                disabled={object.is_full}
                                min={0}
                                name='freeUnitPrice'
                                onChange={(e) => onSetFreeUnitPrice(Number(e.target.value) >= 0 ? Number(e.target.value) : 0)}
                                type='number'
                                value={freeUnitPrice.toString() || ''}
                            />
                            <span>€</span>
                        </div>
                    ) : (
                        <div className={`t-816-variant-price ${deviceType}`}>
                            <span>{object.unit_price} {reduxCacheSite.portfolio?.currency?.symbol}</span>
                        </div>
                    )
                }
                <div className={`t-816-input-wrap ${deviceType}`}>
                    {!object.is_full && (
                        <>
                            <input
                                min={0}
                                max={remainingTickets !== null ? remainingTickets + qty : undefined}
                                name='qty'
                                onChange={(e) => onSetQty(Number(e.target.value) >= 0 ? Number(e.target.value) : 0)}
                                type='number'
                                value={qty.toString() || ''}
                            />
                            <span>Qté</span>
                        </>
                    )}
                    {object.is_full && <span className='t-816-name-full'>Complet</span>}
                </div>
            </div>
        </div>
    )
})
